// Results page component for displaying test results

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DetailedTestResult } from '../types';
import { testsAPI, adminAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Results: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [result, setResult] = useState<DetailedTestResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (testId) {
      loadTestResult(parseInt(testId));
    }
  }, [testId]);

  const loadTestResult = async (id: number) => {
    try {
      // Use admin API if user is admin, otherwise use regular API
      const testResult = user?.is_admin 
        ? await adminAPI.getTestResultDetails(id)
        : await testsAPI.getTestResult(id);
      setResult(testResult);
    } catch (err: any) {
      setError('Failed to load test results. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getLevelColor = (level: string) => {
    const colors: { [key: string]: string } = {
      A1: 'bg-red-100 text-red-800',
      A2: 'bg-orange-100 text-orange-800',
      B1: 'bg-yellow-100 text-yellow-800',
      B2: 'bg-blue-100 text-blue-800',
      C1: 'bg-green-100 text-green-800',
      C2: 'bg-purple-100 text-purple-800',
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  const getLevelDescription = (level: string) => {
    const descriptions: { [key: string]: string } = {
      A1: 'Beginner - Can understand and use familiar everyday expressions',
      A2: 'Elementary - Can understand sentences and frequently used expressions',
      B1: 'Intermediate - Can understand the main points of clear standard input',
      B2: 'Upper Intermediate - Can understand the main ideas of complex text',
      C1: 'Advanced - Can understand a wide range of demanding, longer texts',
    };
    return descriptions[level] || 'Unknown level';
  };

  // Analyze grammar categories for issues
  const analyzeGrammarIssues = () => {
    if (!result) return { categories: [], totalIssues: 0 };
    
    const categoryIssues: { [key: string]: number } = {};
    let totalIssues = 0;
    
    result.user_answers.forEach(answer => {
      if (!answer.is_correct && answer.grammar_category) {
        categoryIssues[answer.grammar_category] = (categoryIssues[answer.grammar_category] || 0) + 1;
        totalIssues++;
      }
    });
    
    const categories = Object.entries(categoryIssues)
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count);
    
    
    return { categories, totalIssues };
  };

  const grammarAnalysis = analyzeGrammarIssues();

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto text-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
        <button
          onClick={() => navigate('/')}
          className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          Go Home
        </button>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="max-w-2xl mx-auto text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Test Result Not Found</h1>
        <p className="text-gray-600 mb-6">
          The requested test result could not be found.
        </p>
        <button
          onClick={() => navigate('/')}
          className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          Go Home
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Test Results</h1>
        <p className="text-gray-600">
          Completed on {new Date(result.completed_at).toLocaleDateString()}
        </p>
      </div>

      {/* Score Summary */}
      <div className="bg-white rounded-lg shadow-md p-8 mb-8">
        <div className="grid md:grid-cols-3 gap-8 text-center">
          <div>
            <div className="text-4xl font-bold text-primary-600 mb-2">
              {result.score_percentage.toFixed(1)}%
            </div>
            <div className="text-gray-600">Overall Score</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-gray-900 mb-2">
              {result.correct_answers}/{result.total_questions}
            </div>
            <div className="text-gray-600">Correct Answers</div>
          </div>
          <div>
            <div className={`inline-block px-4 py-2 rounded-full text-lg font-semibold mb-2 ${getLevelColor(result.english_level)}`}>
              {result.english_level}
            </div>
            <div className="text-gray-600">English Level</div>
          </div>
        </div>
      </div>

      {/* Level Description */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Your English Level</h2>
        <p className="text-gray-700 mb-4">{getLevelDescription(result.english_level)}</p>
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-2">What this means:</h3>
          <p className="text-gray-700 text-sm">
            Based on your test performance, you have demonstrated proficiency at the {result.english_level} level. 
            This level indicates your current ability to understand and use English in various contexts.
          </p>
        </div>
      </div>

      {/* Grammar Issues Analysis */}
      {grammarAnalysis.totalIssues > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Grammar Areas to Focus On</h2>
          <p className="text-gray-700 mb-4">
            You had issues with {grammarAnalysis.totalIssues} question{grammarAnalysis.totalIssues !== 1 ? 's' : ''} across these grammar categories:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {grammarAnalysis.categories.map((item, index) => (
              <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h3 className="font-semibold text-red-800 mb-2">{item.category}</h3>
                <p className="text-red-700 text-sm">
                  {item.count} question{item.count !== 1 ? 's' : ''} incorrect
                </p>
              </div>
            ))}
          </div>
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-sm">
              <strong>Tip:</strong> Click "Show Question Details" below to see specific explanations for each incorrect answer and learn why the correct answer is right.
            </p>
          </div>
        </div>
      )}

      {/* No Grammar Data Available */}
      {result.score_percentage < 100 && grammarAnalysis.totalIssues === 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Test Analysis</h2>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800 text-sm">
              <strong>Note:</strong> Grammar analysis is not available for this test. The detailed explanations will be added soon.
            </p>
          </div>
        </div>
      )}

      {/* Perfect Score Message */}
      {result.score_percentage === 100 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-green-600 mb-2">Perfect Score!</h2>
            <p className="text-gray-700">
              Congratulations! You answered all questions correctly. This indicates excellent understanding of the grammar concepts tested.
            </p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-center space-x-4 mb-8">
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
        >
          {showDetails ? 'Hide Details' : 'Show Question Details'}
        </button>
        <button
          onClick={() => navigate('/test')}
          className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Take Test Again
        </button>
        <button
          onClick={() => navigate('/profile')}
          className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
        >
          View Profile
        </button>
      </div>

      {/* Detailed Results */}
      {showDetails && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Question-by-Question Results</h2>
          <div className="space-y-6">
            {result.user_answers
              .sort((a, b) => a.question_number - b.question_number)
              .map((answer, index) => (
              <div
                key={answer.question_id}
                className={`p-6 border-2 rounded-lg ${
                  answer.is_correct
                    ? 'border-green-200 bg-green-50'
                    : 'border-red-200 bg-red-50'
                }`}
              >
                {/* Question Header */}
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Question {answer.question_number}
                  </h3>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      answer.is_correct
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {answer.is_correct ? '✓ Correct' : '✗ Incorrect'}
                  </span>
                </div>

                {/* Question Text */}
                <div className="mb-4">
                  <p className="text-gray-800 text-base leading-relaxed">
                    {answer.question_text}
                  </p>
                </div>

                {/* Answer Choices */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  {[
                    { key: 'a', text: answer.option_a },
                    { key: 'b', text: answer.option_b },
                    { key: 'c', text: answer.option_c },
                    { key: 'd', text: answer.option_d }
                  ].map((option) => {
                    const isUserAnswer = option.key === answer.user_answer.toLowerCase();
                    const isCorrectAnswer = option.key === answer.correct_answer.toLowerCase();
                    
                    let bgColor = 'bg-gray-100';
                    let textColor = 'text-gray-700';
                    let borderColor = 'border-gray-300';
                    
                    if (isCorrectAnswer) {
                      bgColor = 'bg-green-100';
                      textColor = 'text-green-800';
                      borderColor = 'border-green-300';
                    } else if (isUserAnswer && !answer.is_correct) {
                      bgColor = 'bg-red-100';
                      textColor = 'text-red-800';
                      borderColor = 'border-red-300';
                    }
                    
                    return (
                      <div
                        key={option.key}
                        className={`p-3 rounded-lg border-2 ${bgColor} ${textColor} ${borderColor}`}
                      >
                        <div className="flex items-center">
                          <span className="font-semibold mr-2">
                            {option.key.toUpperCase()})
                          </span>
                          <span>{option.text}</span>
                          {isCorrectAnswer && (
                            <span className="ml-auto text-green-600 font-bold">✓</span>
                          )}
                          {isUserAnswer && !answer.is_correct && (
                            <span className="ml-auto text-red-600 font-bold">✗</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Answer Summary */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                    <div className="text-sm">
                      <span className="font-medium text-gray-700">Your answer: </span>
                      <span className={`font-semibold ${
                        answer.is_correct ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {answer.user_answer.toUpperCase()}
                      </span>
                    </div>
                    {!answer.is_correct && (
                      <div className="text-sm">
                        <span className="font-medium text-gray-700">Correct answer: </span>
                        <span className="font-semibold text-green-700">
                          {answer.correct_answer.toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Grammar Explanation for Incorrect Answers */}
                  {!answer.is_correct && answer.grammar_explanation && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <span className="text-red-600 text-lg">💡</span>
                        </div>
                        <div className="ml-3">
                          <h4 className="text-sm font-semibold text-red-800 mb-1">
                            {answer.grammar_category ? `${answer.grammar_category}: ` : ''}Why this is wrong
                          </h4>
                          <p className="text-sm text-red-700">
                            {answer.grammar_explanation}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Grammar Category for Correct Answers */}
                  {answer.is_correct && answer.grammar_category && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-3">
                      <div className="flex items-center">
                        <span className="text-green-600 text-lg mr-2">✓</span>
                        <span className="text-sm text-green-800">
                          <strong>{answer.grammar_category}</strong> - You correctly applied this grammar rule!
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Results;
